import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMeasure } from 'react-use';
import { motion } from 'motion/react';
import {
  Collapsible,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { Icon, Tooltip } from '../../../components/ui/index';
import { cn } from '@cotera/client/v0/lib/utils';
import { useCocoChatModel } from '../models/coco-chat.model';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import {
  getLastCancelledErrorFromStackFrames,
  getStackFrameStatusInfo,
  pickMessageForKey,
  type StackFrameStatusKey,
} from '../utils/coco-stack-frame-status';
import { AnimatedEllipsis } from '@cotera/client/v0/components/ui/animated-ellipsis';
import { ShimmerText } from '@cotera/client/v0/components/ui/shimmer-text';
import type { ChatStatusFrameOutput } from '@cotera/api';
import { coco, ResourceType } from '@cotera/api';
import { Personality } from '../../etc/personality';
import { useAppModel } from '../../shell/app.model';
import { CocoOutOfCreditsBanner } from './coco-out-of-credits-banner';
import { formatStackFrameLastErrorForDisplay } from '../../etc/format-stack-frame-last-error';
import { useToolsAsync } from '../../../hooks/use-tools-info';
import {
  agentKeyFromToolKey,
  formatAgentKeyForDisplay,
  useAgentsDisplayNames,
  useAgentsEmojiByKey,
  useAgentsIdByKey,
} from '../../../hooks/use-agents-display-names';
import { useModalManager } from '../../../components/app/modal-manager/modal-manager';
import { useAvailableTools } from '../../../hooks/use-available-tools';
import { filterDisplayableLoadedTools } from '../actions/view-loaded-tools.action';
import { AddResourceArtifactAction } from '../actions/coco-add-resource-artifact.action';
import { RemoveLoadedToolAction } from '../actions/coco-remove-loaded-tool.action';
import { UnfocusAgentAction } from '../actions/coco-unfocus-agent.action';
import { Button } from '@cotera/client/v0/components/ui/button';
import { getProviderFromToolKey } from '@cotera/client/v0/components/tool-connections/utils';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections';
import { ProviderDetailPage } from '@cotera/client/v0/components/tool-connections/provider-detail';
import { LoaderIcon } from 'lucide-react';

const { isAgentToolKey, agentIdToAgentToolKey } = coco;

const isFocusedAgentToolKey = (
  toolKey: string,
  focusedAgentId: string | null | undefined
): boolean => {
  if (focusedAgentId === null || focusedAgentId === undefined) {
    return false;
  }
  return agentIdToAgentToolKey(focusedAgentId) === toolKey;
};

export type CocoInputHeaderProps = {
  /** When null, the entire drawer hides (no wrapper rendered). */
  header: React.ReactNode | null;
  /** Content shown when expanded */
  children?: React.ReactNode;
  /** Whether the drawer starts expanded */
  defaultOpen?: boolean;
  /** Additional class names for the header container */
  className?: string;
};

export type StackFrameHeaderProps = {
  personality: Personality;
  stackFrames: ChatStatusFrameOutput[];
};

const MESSAGE_ROTATION_MS = 4000;

/**
 * Hook that stabilises status messages so they rotate every ~2s instead of
 * every poll cycle (~500ms). Transitions immediately when the status key
 * changes (e.g. allocated → thinking).
 */
function useStableStackFrameMessage(
  stackFrames: ChatStatusFrameOutput[],
  personality: Personality
): {
  message: string;
  isTerminal: boolean;
  isComplete: boolean;
  statusKey: StackFrameStatusKey;
  retryCount: number;
} {
  const { statusKey, isTerminal, isComplete, retryCount } =
    getStackFrameStatusInfo(stackFrames);

  const [message, setMessage] = useState(() =>
    pickMessageForKey(statusKey, personality)
  );
  const prevKeyRef = useRef(statusKey);

  // Immediate transition on status key change
  useEffect(() => {
    if (statusKey !== prevKeyRef.current) {
      prevKeyRef.current = statusKey;
      setMessage(pickMessageForKey(statusKey, personality));
    }
  }, [statusKey, personality]);

  // Rotate every MESSAGE_ROTATION_MS within the same status
  useEffect(() => {
    if (isTerminal) {
      return;
    }
    const id = setInterval(() => {
      setMessage(pickMessageForKey(statusKey, personality));
    }, MESSAGE_ROTATION_MS);
    return () => clearInterval(id);
  }, [statusKey, personality, isTerminal]);

  return { message, isTerminal, isComplete, statusKey, retryCount };
}

const AGENT_CHIP_CLASS = cn(
  'flex items-center gap-1.5 px-2 py-1 rounded-md',
  'bg-muted/50 border border-border/60 text-xs'
);

const NUM_AGENTS_TO_SHOW = 2;

/** Shared hook for tool lookup, agents display names, and sorted keys. */
function useAgentToolKeys(agentToolKeys: string[]) {
  const { tools } = useToolsAsync();
  const agentsDisplayNames = useAgentsDisplayNames();
  const agentsEmojiByKey = useAgentsEmojiByKey();
  const toolByKey = useMemo(() => {
    const map = new Map<string, (typeof tools)[number]>();
    for (const t of tools) {
      map.set(t.key, t);
    }
    return map;
  }, [tools]);
  const sortedKeys = useMemo(
    () => [...agentToolKeys].sort((a, b) => a.localeCompare(b)),
    [agentToolKeys]
  );
  return { toolByKey, agentsDisplayNames, agentsEmojiByKey, sortedKeys };
}

/** Tool lookup by key (from tools list). */
function useToolByKey() {
  const { tools } = useToolsAsync();
  return useMemo(() => {
    const map = new Map<string, (typeof tools)[number]>();
    for (const t of tools) {
      map.set(t.key, t);
    }
    return map;
  }, [tools]);
}

/** Single agent chip. Highlights with primary color when focused. */
const AgentChip: React.FC<{
  displayName: string;
  /** When true, omits truncation (for summary chips like "3 sub agents"). */
  noTruncate?: boolean;
  /** When true, applies primary color highlight to indicate focused agent */
  isFocused?: boolean;
  /** Optional user-provided emoji shown in place of the default cpu icon. */
  emoji?: string;
}> = ({ displayName, noTruncate, isFocused, emoji }) => {
  const chip = (
    <div
      className={cn(
        AGENT_CHIP_CLASS,
        isFocused === true && 'border-primary/40 text-primary'
      )}
    >
      <div className="flex items-center justify-center w-5 h-5 rounded bg-muted shrink-0">
        {emoji !== undefined && emoji !== '' ? (
          <span className="text-sm leading-none">{emoji}</span>
        ) : (
          <Icon icon="bot" className="size-3.5" />
        )}
      </div>
      <span
        className={cn(
          'font-medium',
          noTruncate === true ? undefined : 'truncate max-w-[120px]'
        )}
      >
        {displayName}
      </span>
    </div>
  );
  return isFocused === true ? (
    <Tooltip
      side="top"
      tooltipContent="Focused – Coco will prioritize this agent"
    >
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

/** Focused agent chip with X on hover to unfocus. */
const FocusedAgentChip: React.FC<{
  displayName: string;
  onUnfocus: () => void;
}> = ({ displayName, onUnfocus }) => {
  return (
    <div
      className={cn(
        'group flex items-center gap-1.5 px-2 py-1 rounded-md',
        'bg-primary/10 border border-primary/40 text-primary'
      )}
    >
      <span className="font-medium truncate max-w-[100px]">{displayName}</span>
      <Tooltip side="top" tooltipContent="Unfocus agent">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          icon="x-mark"
          className="hover:bg-primary/20"
          onClick={(e) => {
            e.stopPropagation();
            onUnfocus();
          }}
          aria-label="Unfocus agent"
        />
      </Tooltip>
    </div>
  );
};

/** Collapsible body content: agents (name, description, focus chip, unload) and tools. */
const AgentToolsListBody: React.FC<{
  agentToolKeys: string[];
  displayableToolKeys: string[];
  focusedAgentId?: string | null;
  agentsIdByKey?: Map<string, string>;
  removingToolKeys: Set<string>;
  onUnloadTool: (toolKey: string, e: React.MouseEvent) => void;
}> = ({
  agentToolKeys,
  displayableToolKeys,
  focusedAgentId,
  agentsIdByKey,
  removingToolKeys,
  onUnloadTool,
}) => {
  const { toolByKey, agentsDisplayNames, agentsEmojiByKey, sortedKeys } =
    useAgentToolKeys(agentToolKeys);
  const allToolByKey = useToolByKey();
  const { providers } = useAvailableTools();
  const modalManager = useModalManager((s) => s.actions);
  const addResourceArtifactAction = useAction(AddResourceArtifactAction);
  const unfocusAgentAction = useAction(UnfocusAgentAction);

  const providerByKey = useMemo(() => {
    const map = new Map<string, (typeof providers)[number]>();
    for (const p of providers) {
      map.set(p.provider, p);
    }
    return map;
  }, [providers]);

  const handleAgentClick = useCallback(
    (agentId: string) => {
      void addResourceArtifactAction.execute({
        resourceType: ResourceType.Agent,
        resourceId: agentId,
      });
    },
    [addResourceArtifactAction]
  );

  const handleUnloadClick = useCallback(
    (toolKey: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onUnloadTool(toolKey, e);
    },
    [onUnloadTool]
  );

  const handleToolClick = useCallback(
    (toolKey: string) => {
      const providerKey = getProviderFromToolKey(toolKey);
      const provider = providerByKey.get(providerKey);
      if (provider === undefined) {
        return;
      }
      modalManager.push({
        size: 'lg',
        view: () => (
          <ProviderDetailPage provider={provider} expandedToolKey={toolKey} />
        ),
      });
    },
    [modalManager, providerByKey]
  );

  const sortedDisplayableTools = useMemo(
    () => [...displayableToolKeys].sort((a, b) => a.localeCompare(b)),
    [displayableToolKeys]
  );

  return (
    <div className="flex flex-col gap-3">
      {sortedKeys.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            Agents
          </div>
          <div className="flex flex-col gap-1.5">
            {sortedKeys.map((key) => {
              const tool = toolByKey.get(key);
              const agentKey = agentKeyFromToolKey(key);
              const displayName =
                tool?.name ??
                (agentKey !== null
                  ? agentsDisplayNames.get(agentKey)
                  : undefined) ??
                (agentKey !== null ? formatAgentKeyForDisplay(agentKey) : key);
              const description = tool?.description ?? '';
              const agentId =
                agentKey !== null ? agentsIdByKey?.get(agentKey) : undefined;
              const isFocused = isFocusedAgentToolKey(key, focusedAgentId);
              const emoji =
                agentKey !== null ? agentsEmojiByKey.get(agentKey) : undefined;

              return (
                <div
                  key={key}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 rounded-lg border border-border/60',
                    'bg-card hover:bg-accent/30 hover:border-primary/20 cursor-pointer transition-colors'
                  )}
                  onClick={() => {
                    if (agentId !== undefined) {
                      handleAgentClick(agentId);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center justify-center w-6 h-6 rounded bg-muted shrink-0">
                        {emoji !== undefined && emoji !== '' ? (
                          <span className="text-base leading-none">
                            {emoji}
                          </span>
                        ) : (
                          <Icon icon="bot" className="size-3.5" />
                        )}
                      </div>
                      <span className="font-medium text-sm truncate">
                        {displayName}
                      </span>
                      {isFocused && (
                        <FocusedAgentChip
                          displayName="Focused"
                          onUnfocus={() => {
                            void unfocusAgentAction.execute({});
                          }}
                        />
                      )}
                    </div>
                    {description !== '' && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 pl-8">
                        {description}
                      </p>
                    )}
                  </div>
                  <Tooltip
                    side="top"
                    tooltipContent={
                      removingToolKeys.has(key)
                        ? 'Removing...'
                        : 'Remove from context'
                    }
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      icon={removingToolKeys.has(key) ? undefined : 'x-mark'}
                      className="opacity-0 group-hover:opacity-100 shrink-0 size-6 min-w-6 p-0 hover:bg-destructive/20 hover:text-destructive disabled:opacity-100"
                      onClick={(e) => handleUnloadClick(key, e)}
                      aria-label="Unload agent"
                      disabled={removingToolKeys.has(key)}
                    >
                      {removingToolKeys.has(key) && (
                        <LoaderIcon className="size-3.5 animate-spin" />
                      )}
                    </Button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {sortedDisplayableTools.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            Tools
          </div>
          <div className="flex flex-col gap-1.5">
            {sortedDisplayableTools.map((key) => {
              const tool = allToolByKey.get(key);
              const displayName = tool?.name ?? key;
              const providerKey = getProviderFromToolKey(key);
              const ProviderIcon =
                PROVIDER_ICONS[providerKey as keyof typeof PROVIDER_ICONS] ??
                (() => null);

              return (
                <div
                  key={key}
                  className={cn(
                    'group flex items-center gap-3 px-3 py-2 rounded-lg border border-border/60',
                    'bg-card hover:bg-accent/30 hover:border-primary/20 cursor-pointer transition-colors'
                  )}
                  onClick={() => handleToolClick(key)}
                >
                  <div className="flex items-center justify-center w-6 h-6 rounded-md bg-muted/30 shrink-0">
                    <ProviderIcon />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm truncate block">
                      {displayName}
                    </span>
                  </div>
                  <Tooltip
                    side="top"
                    tooltipContent={
                      removingToolKeys.has(key)
                        ? 'Removing...'
                        : 'Remove from context'
                    }
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      icon={removingToolKeys.has(key) ? undefined : 'x-mark'}
                      className="opacity-0 group-hover:opacity-100 shrink-0 size-6 min-w-6 p-0 hover:bg-destructive/20 hover:text-destructive disabled:opacity-100"
                      onClick={(e) => handleUnloadClick(key, e)}
                      aria-label="Unload tool"
                      disabled={removingToolKeys.has(key)}
                    >
                      {removingToolKeys.has(key) && (
                        <LoaderIcon className="size-3.5 animate-spin" />
                      )}
                    </Button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/** Agent tools (coco sub-agents or defined agents) in context. When > 2, shows "x agents" summary; body shows in CocoInputHeader drawer. */
const AgentToolsInContextHeader: React.FC<{
  agentToolKeys: string[];
  focusedAgentId?: string | null;
  agentsIdByKey?: Map<string, string>;
}> = ({ agentToolKeys, focusedAgentId }) => {
  const count = agentToolKeys.length;
  const { toolByKey, agentsDisplayNames, agentsEmojiByKey, sortedKeys } =
    useAgentToolKeys(agentToolKeys);
  const showCollapsed = count > NUM_AGENTS_TO_SHOW;

  if (showCollapsed) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <AgentChip
          displayName={`${count} sub agent${count !== 1 ? 's' : ''}`}
          noTruncate
        />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {sortedKeys.map((key) => {
        const tool = toolByKey.get(key);
        const agentKey = agentKeyFromToolKey(key);
        const displayName =
          tool?.name ??
          (agentKey !== null ? agentsDisplayNames.get(agentKey) : undefined) ??
          (agentKey !== null ? formatAgentKeyForDisplay(agentKey) : key);
        const isFocused = isFocusedAgentToolKey(key, focusedAgentId);
        const emoji =
          agentKey !== null ? agentsEmojiByKey.get(agentKey) : undefined;

        return (
          <AgentChip
            key={key}
            displayName={displayName}
            isFocused={isFocused}
            {...(emoji !== undefined ? { emoji } : {})}
          />
        );
      })}
    </div>
  );
};

export type HeaderContentResult =
  | { header: React.ReactNode; children?: undefined }
  | { header: React.ReactNode; children: React.ReactNode };

/** Hook that returns header content or null. When > 2 agents, children is the expandable body (agent list). */
export const useHeaderContent = (
  personality: Personality
): HeaderContentResult | null => {
  const model = useCocoChatModel();
  const appModel = useAppModel();
  const conversation = useWatchableValue(model.conversationWatchable);
  const agentsIdByKey = useAgentsIdByKey();
  const stackFrames = useWatchableValue(model.stackFramesWatchable);
  const loadedToolKeys = useWatchableValue(model.loadedToolsWatchable);
  const removeLoadedToolAction = useAction(RemoveLoadedToolAction);
  const { isComplete, statusKey, lastError } = getStackFrameStatusInfo(
    stackFrames as ChatStatusFrameOutput[]
  );
  const lastCancelledError = getLastCancelledErrorFromStackFrames(
    stackFrames as ChatStatusFrameOutput[]
  );

  const balance = useWatchableValue(appModel.creditBalance);
  const billingKind = useWatchableValue(appModel.billingKind);

  const focusedAgentId = conversation?.getFocusedAgentId() ?? null;

  const [removedToolKeys, setRemovedToolKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [removingToolKeys, setRemovingToolKeys] = useState<Set<string>>(
    () => new Set()
  );

  // Clear optimistic state when loaded tools change from server (e.g. after successful remove)
  useEffect(() => {
    setRemovedToolKeys((prev) => {
      const next = new Set(prev);
      for (const key of prev) {
        if (!loadedToolKeys.includes(key)) {
          next.delete(key);
        }
      }
      return next;
    });
  }, [loadedToolKeys]);

  const handleUnloadTool = useCallback(
    (toolKey: string, _e: React.MouseEvent) => {
      setRemovedToolKeys((prev) => new Set(prev).add(toolKey));
      setRemovingToolKeys((prev) => new Set(prev).add(toolKey));
      void removeLoadedToolAction
        .execute({ toolKey })
        .then((result) => {
          if (result.isErr()) {
            setRemovedToolKeys((prev) => {
              const next = new Set(prev);
              next.delete(toolKey);
              return next;
            });
          }
        })
        .finally(() => {
          setRemovingToolKeys((prev) => {
            const next = new Set(prev);
            next.delete(toolKey);
            return next;
          });
        });
    },
    [removeLoadedToolAction]
  );

  return useMemo(() => {
    // billingKind is null when billingInfo hasn't loaded yet or doesn't exist for
    // the customer (e.g. a demo org account) — don't show the banner in that case.
    if (
      balance <= 0 &&
      billingKind !== null &&
      billingKind !== 'orb-enterprise'
    ) {
      return { header: <CocoOutOfCreditsBanner /> };
    }
    const agentToolKeys = loadedToolKeys.filter((k) => isAgentToolKey(k));
    const displayableToolKeys = filterDisplayableLoadedTools(
      loadedToolKeys
    ).filter((k) => !isAgentToolKey(k));
    const filteredAgentToolKeys = agentToolKeys.filter(
      (k) => !removedToolKeys.has(k)
    );
    const filteredDisplayableToolKeys = displayableToolKeys.filter(
      (k) => !removedToolKeys.has(k)
    );
    const showStackFrameStatus = !isComplete || statusKey === 'cancelled';
    const loadingContent = showStackFrameStatus ? (
      <StackFrameHeader personality={personality} stackFrames={stackFrames} />
    ) : null;
    const agentsContent =
      filteredAgentToolKeys.length > 0 ? (
        <AgentToolsInContextHeader
          agentToolKeys={filteredAgentToolKeys}
          focusedAgentId={focusedAgentId}
          agentsIdByKey={agentsIdByKey}
        />
      ) : filteredDisplayableToolKeys.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <AgentChip
            displayName={`${filteredDisplayableToolKeys.length} tool${
              filteredDisplayableToolKeys.length !== 1 ? 's' : ''
            } loaded`}
            noTruncate
          />
        </div>
      ) : null;

    const headerContent =
      agentsContent !== null && loadingContent !== null ? (
        <div className="flex items-center justify-between gap-3 w-full">
          {agentsContent}
          <div className="shrink-0">{loadingContent}</div>
        </div>
      ) : agentsContent !== null ? (
        agentsContent
      ) : loadingContent !== null ? (
        loadingContent
      ) : null;

    if (headerContent === null) {
      return null;
    }

    const hasAgentTools =
      filteredAgentToolKeys.length > 0 ||
      filteredDisplayableToolKeys.length > 0;
    const hasErrorDetails =
      (statusKey === 'failed' || statusKey === 'retrying') &&
      lastError !== undefined &&
      lastError !== '';
    const hasCancellationDetails =
      statusKey === 'cancelled' || lastCancelledError !== undefined;
    const hasExpandableBody =
      hasAgentTools || hasErrorDetails || hasCancellationDetails;

    const lastErrorDisplay =
      lastError !== undefined && lastError !== ''
        ? formatStackFrameLastErrorForDisplay(lastError)
        : '';
    const cancellationErrorToDisplay =
      statusKey === 'cancelled' ? lastError : lastCancelledError;
    const cancellationErrorDisplay =
      cancellationErrorToDisplay !== undefined &&
      cancellationErrorToDisplay !== ''
        ? formatStackFrameLastErrorForDisplay(cancellationErrorToDisplay)
        : '';

    const bodyContent = (
      <>
        {hasCancellationDetails && (
          <div className="flex flex-col gap-2 mb-3">
            <div className="text-[11px] font-medium text-yellow-900 dark:text-yellow-200 uppercase tracking-wide">
              Cancelled
            </div>
            <div
              className={cn(
                'text-xs p-3 rounded-lg flex flex-col gap-2',
                'bg-yellow-500/15 border border-yellow-500/35 text-yellow-900 dark:text-yellow-100'
              )}
            >
              <p>
                {statusKey === 'cancelled'
                  ? 'This run was stopped before it finished.'
                  : 'A previous run was stopped before it finished.'}
              </p>
              {cancellationErrorToDisplay !== undefined &&
                cancellationErrorToDisplay !== '' && (
                  <>
                    <div className="text-[11px] font-medium text-yellow-950/80 dark:text-yellow-200/90 uppercase tracking-wide border-t border-yellow-500/25 pt-2">
                      Error before stop
                    </div>
                    <pre
                      className={cn(
                        'text-xs p-2 rounded-md overflow-x-auto overflow-y-auto max-h-40',
                        'bg-yellow-950/5 dark:bg-yellow-950/30 border border-yellow-600/20',
                        'text-yellow-950 dark:text-yellow-50',
                        'whitespace-pre-wrap break-words'
                      )}
                    >
                      {cancellationErrorDisplay}
                    </pre>
                  </>
                )}
            </div>
          </div>
        )}
        {hasErrorDetails && (
          <div className="flex flex-col gap-2 mb-3">
            <div className="text-[11px] font-medium text-destructive uppercase tracking-wide">
              Error details
            </div>
            <pre
              className={cn(
                'text-xs p-3 rounded-lg overflow-x-auto overflow-y-auto',
                'bg-destructive/10 border border-destructive/20 text-destructive',
                'whitespace-pre-wrap break-words max-h-48'
              )}
            >
              {lastErrorDisplay}
            </pre>
          </div>
        )}
        {hasAgentTools && (
          <AgentToolsListBody
            agentToolKeys={filteredAgentToolKeys}
            displayableToolKeys={filteredDisplayableToolKeys}
            focusedAgentId={focusedAgentId}
            agentsIdByKey={agentsIdByKey}
            removingToolKeys={removingToolKeys}
            onUnloadTool={handleUnloadTool}
          />
        )}
      </>
    );

    return hasExpandableBody
      ? {
          header: headerContent,
          children: bodyContent,
        }
      : { header: headerContent };
  }, [
    balance,
    billingKind,
    isComplete,
    personality,
    stackFrames,
    statusKey,
    lastError,
    lastCancelledError,
    loadedToolKeys,
    focusedAgentId,
    agentsIdByKey,
    removedToolKeys,
    removingToolKeys,
    handleUnloadTool,
  ]);
};

export const StackFrameHeader: React.FC<StackFrameHeaderProps> = ({
  personality,
  stackFrames,
}) => {
  const { message, isTerminal, statusKey, retryCount } =
    useStableStackFrameMessage(
      stackFrames as ChatStatusFrameOutput[],
      personality
    );

  if (message === '') {
    return null;
  }

  const isRetrying = statusKey === 'retrying';
  const isFailed = statusKey === 'failed';
  const isCancelled = statusKey === 'cancelled';

  const content = (
    <>
      {message}
      {!isTerminal && <AnimatedEllipsis className="text-left" />}
      {isRetrying && retryCount > 0 && (
        <span className="ml-1 opacity-70 text-[10px]">
          (attempt {retryCount + 1})
        </span>
      )}
    </>
  );

  return (
    <div
      className={cn(
        'flex items-center justify-start gap-0.5 px-2 py-1 rounded-md text-xs',
        isFailed &&
          'bg-destructive/10 text-destructive border border-destructive/20',
        isRetrying &&
          'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
        isCancelled &&
          'bg-yellow-500/15 text-yellow-900 dark:text-yellow-100 border border-yellow-500/35'
      )}
    >
      {isFailed || isRetrying || isCancelled ? (
        <span>{content}</span>
      ) : (
        <ShimmerText variant="muted" className="text-xs">
          {content}
        </ShimmerText>
      )}
    </div>
  );
};

export const CocoInputHeader: React.FC<CocoInputHeaderProps> = ({
  header,
  children,
  defaultOpen = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [contentRef, { height: contentHeight }] = useMeasure<HTMLDivElement>();
  const [wrapperRef] = useMeasure<HTMLDivElement>();
  const isVisible = header !== null && header !== undefined;
  const hasBody = children !== null && children !== undefined;

  const content = (
    <div className="bg-background border-x w-[95%] mx-auto px-3 py-2 text-xs max-h-64 overflow-y-auto">
      {children}
    </div>
  );

  const headerBar = (
    <div
      className={cn(
        'flex items-center justify-between w-full px-3 py-2 text-xs',
        'bg-background rounded-t-xl',
        'border-t border-x w-[95%] mx-auto',
        className
      )}
    >
      {hasBody ? (
        <div className="flex items-center gap-2 w-full">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 text-foreground text-xs cursor-pointer hover:opacity-80 transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded"
            >
              <Icon
                icon={isOpen ? 'chevron-down' : 'chevron-right'}
                className="size-4 shrink-0"
              />
            </button>
          </CollapsibleTrigger>
          {header}
        </div>
      ) : (
        header
      )}
    </div>
  );

  if (!isVisible) {
    return null;
  }

  const drawer = hasBody ? (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      {headerBar}
      <div className="relative w-full">
        {/* Hidden measurement div - always full size for accurate height */}
        <div
          ref={contentRef}
          className="invisible absolute top-0 left-1/2 -translate-x-1/2 w-[95%] pointer-events-none"
          aria-hidden
        >
          {content}
        </div>
        <motion.div
          initial={false}
          animate={{ height: isOpen ? contentHeight : 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="overflow-hidden w-full"
        >
          {content}
        </motion.div>
      </div>
    </Collapsible>
  ) : (
    headerBar
  );

  return <div ref={wrapperRef}>{drawer}</div>;
};
